import { useCallback, useEffect, useRef, useState } from 'react';
import { Bouton, IconePleinEcran, IconeQuitterPleinEcran, Message } from '@journal/ui';
import { useSession } from '../lib/session.tsx';

/**
 * Agenda — une page par jour, réglée, où l'on écrit librement.
 *
 * ── Ce qu'il est, et ce qu'il n'est pas ─────────────────────────────────────
 * Ni le cahier journal, qui décrit des séances avec objectifs et matériel et
 * qu'on montre à un inspecteur. Ni le pense-bête, qui liste des courses à
 * cocher. Le carnet où l'on note ce qui n'entre dans aucune case : un mot à
 * dire à un parent, une idée pour demain, ce qui s'est passé à la récréation.
 *
 * Repris de la v1 à fonctionnalités égales : petit calendrier à pastilles,
 * navigation au jour, taille de texte réglable, couleurs de texte et de
 * surlignage, enregistrement automatique. Écrit en Marelle, l'écriture
 * scolaire du ministère, et sans impression : c'est un carnet d'écran.
 *
 * ── Pourquoi une zone éditable et non un champ de texte ─────────────────────
 * Parce qu'on y met des couleurs. Un `<textarea>` ne porte pas de mise en
 * forme, et l'agenda de la v1 s'en sert : une échéance en rouge, un rappel
 * surligné. La contrepartie est que le contenu est du HTML, ce qui impose au
 * serveur de le nettoyer — cette page est relue par un remplaçant ou par la
 * direction.
 *
 * ── Le contenu n'est JAMAIS piloté par le rendu React ───────────────────────
 * Il est posé dans la zone à l'ouverture d'une journée, et plus jamais ensuite.
 * Le rattacher à un état ferait réécrire la zone à chaque frappe, et le curseur
 * sauterait en fin de texte à chaque lettre. C'est la raison des références
 * plutôt que des états pour tout ce qui touche à la saisie.
 */

const MOIS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
] as const;

const JOURS = [
  'dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi',
] as const;

/** Bornes de la taille du texte, en pixels. Reprises de la v1. */
const TAILLE_MIN = 12;
const TAILLE_MAX = 30;
const TAILLE_PAS = 2;
const TAILLE_DEFAUT = 17;
const CLE_TAILLE = 'agenda.taille';

const COULEURS_TEXTE = [
  '#1b3a6b', '#000000', '#2563a8', '#3a7ad4', '#c0392b',
  '#e67e22', '#27ae60', '#8e44ad', '#555555',
] as const;

const COULEURS_SURLIGNE = [
  '#fef9c3', '#fed7aa', '#bbf7d0', '#bfdbfe',
  '#fce7f3', '#ede9fe', '#fee2e2', '#ccfbf1',
] as const;

/**
 * Délai avant enregistrement, après la dernière frappe.
 *
 * 900 ms comme dans la v1 : assez pour ne pas écrire à chaque lettre, assez
 * court pour qu'une interruption — téléphone, fin de récréation — ne perde
 * rien.
 */
const DELAI_ENREGISTREMENT = 900;

// ── Dates ────────────────────────────────────────────────────────────────────
/*
 * Arithmétique en UTC, comme partout ailleurs dans l'application. Passer par
 * l'heure locale décale d'un jour selon le fuseau, et un agenda décalé d'un
 * jour est pire qu'un agenda vide.
 */

function cleDe(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
    + `-${String(d.getUTCDate()).padStart(2, '0')}`;
}

function dateDe(cle: string): Date {
  const [a, m, j] = cle.split('-').map(Number);
  return new Date(Date.UTC(a ?? 1970, (m ?? 1) - 1, j ?? 1));
}

export function decaler(cle: string, jours: number): string {
  const d = dateDe(cle);
  d.setUTCDate(d.getUTCDate() + jours);
  return cleDe(d);
}

/** Aujourd'hui, lu sur l'horloge LOCALE : c'est la date qu'affiche le poste. */
function aujourdhui(): string {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`
    + `-${String(n.getDate()).padStart(2, '0')}`;
}

/** Mercredi, samedi, dimanche : les jours sans classe de la v1. */
const SANS_CLASSE = new Set([3, 6, 0]);

export function estJourDeClasse(cle: string): boolean {
  return !SANS_CLASSE.has(dateDe(cle).getUTCDay());
}

/**
 * La prochaine journée de classe APRÈS celle-ci.
 *
 * L'agenda s'ouvre là : c'est la journée qu'on prépare en sortant de celle du
 * jour. S'ouvrir sur aujourd'hui donnerait une page qu'on a déjà écrite.
 */
export function prochainJourDeClasse(depuis: string): string {
  let cle = decaler(depuis, 1);
  for (let garde = 0; garde < 10 && !estJourDeClasse(cle); garde += 1) {
    cle = decaler(cle, 1);
  }
  return cle;
}

/** Numéro de semaine ISO-8601. */
export function numeroSemaine(cle: string): number {
  const d = dateDe(cle);
  d.setUTCDate(d.getUTCDate() + 3 - ((d.getUTCDay() + 6) % 7));
  const premiere = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  return 1 + Math.round(
    ((d.getTime() - premiere.getTime()) / 86_400_000 - 3
      + ((premiere.getUTCDay() + 6) % 7)) / 7,
  );
}

/** « jeudi 10 septembre 2026 » — le nom accessible de la zone d'écriture. */
export function libelleJour(cle: string): string {
  const d = dateDe(cle);
  return `${JOURS[d.getUTCDay()]} ${d.getUTCDate()} `
    + `${MOIS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** Les cases du mois affiché : `null` pour le décalage avant le 1er. */
export function grilleDuMois(annee: number, mois: number): Array<string | null> {
  const premier = new Date(Date.UTC(annee, mois - 1, 1)).getUTCDay();
  // La semaine commence le LUNDI : dimanche (0) se décale de six cases.
  const decalage = premier === 0 ? 6 : premier - 1;
  const nbJours = new Date(Date.UTC(annee, mois, 0)).getUTCDate();

  const cases: Array<string | null> = Array.from({ length: decalage }, () => null);
  for (let j = 1; j <= nbJours; j += 1) {
    cases.push(`${annee}-${String(mois).padStart(2, '0')}-${String(j).padStart(2, '0')}`);
  }
  return cases;
}

/*
 * La VACUITÉ n'est pas calculée ici.
 *
 * Le serveur la décide — il nettoie le texte avant de la tester — et rend
 * `emptied` avec la réponse. La recalculer à l'écran ferait deux règles à tenir
 * d'accord, qui divergeraient au premier collage écarté : la pastille du
 * calendrier resterait allumée sur une journée que la base vient de supprimer.
 */

function lireTaille(): number {
  try {
    const v = Number.parseInt(localStorage.getItem(CLE_TAILLE) ?? '', 10);
    if (!Number.isFinite(v)) return TAILLE_DEFAUT;
    return Math.min(TAILLE_MAX, Math.max(TAILLE_MIN, v));
  } catch {
    return TAILLE_DEFAUT;
  }
}

interface Page {
  date: string;
  content: string;
  updatedAt: string | null;
}

export function Agenda() {
  const { api } = useSession();

  const [date, setDate] = useState(() => prochainJourDeClasse(aujourdhui()));
  const [mois, setMois] = useState(() => {
    const d = dateDe(prochainJourDeClasse(aujourdhui()));
    return { annee: d.getUTCFullYear(), mois: d.getUTCMonth() + 1 };
  });
  const [joursPleins, setJoursPleins] = useState<Set<string>>(new Set());
  const [etat, setEtat] = useState('');
  const [taille, setTaille] = useState(lireTaille);
  const [palette, setPalette] = useState<'texte' | 'surligne' | null>(null);
  const [couleurTexte, setCouleurTexte] = useState<string>(COULEURS_TEXTE[0]);
  const [couleurSurligne, setCouleurSurligne] = useState<string>('transparent');
  const [erreur, setErreur] = useState<string | null>(null);
  const [pleinEcran, setPleinEcran] = useState(false);

  const zone = useRef<HTMLDivElement>(null);
  /** Le cadre de l'agenda : c'est lui, et lui seul, qui passe en plein écran. */
  const cadre = useRef<HTMLDivElement>(null);
  /**
   * La journée est-elle CHARGÉE ?
   *
   * Tant qu'elle ne l'est pas, la zone est vide parce qu'elle vient d'être
   * créée, non parce qu'on l'a effacée. Enregistrer dans cet état effaçait la
   * journée — c'est le défaut que ce drapeau ferme, et il vient de la v1.
   */
  const chargee = useRef(false);
  const dernierEnvoi = useRef('');
  const minuterie = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** La sélection d'avant l'ouverture de la palette, à restaurer. */
  const selection = useRef<Range | null>(null);

  // ── Enregistrement ────────────────────────────────────────────────────────
  const enAttente = useCallback(() => {
    if (!chargee.current) return false;
    const el = zone.current;
    return !!el && el.innerHTML !== dernierEnvoi.current;
  }, []);

  /**
   * Enregistre maintenant, sans attendre le délai.
   *
   * Appelé aussi avant de changer de journée et avant d'imprimer : quitter une
   * page sans écrire ce qu'on vient d'y taper serait la perte la plus banale
   * qu'un agenda puisse produire.
   */
  const enregistrer = useCallback(async (cle: string, options?: { auDepart?: boolean }) => {
    if (minuterie.current) clearTimeout(minuterie.current);
    const el = zone.current;
    if (!el || !enAttente()) return;

    const content = el.innerHTML;
    try {
      const reponse = await api.requete<{ emptied: boolean; content: string }>(
        `/agenda/${cle}`,
        {
          methode: 'PUT',
          corps: { content },
          ...(options?.auDepart ? { garderEnVie: true } : {}),
        },
      );
      dernierEnvoi.current = content;
      setEtat('enregistré');
      setErreur(null);

      // La pastille du petit calendrier suit l'état réel de la journée.
      setJoursPleins((actuels) => {
        const suivants = new Set(actuels);
        if (reponse.emptied) suivants.delete(cle);
        else suivants.add(cle);
        return suivants;
      });
    } catch (e) {
      setEtat('non enregistré');
      setErreur(e instanceof Error ? e.message : 'Enregistrement impossible');
    }
  }, [api, enAttente]);

  const programmerEnregistrement = useCallback((delai = DELAI_ENREGISTREMENT) => {
    setEtat('modifié…');
    if (minuterie.current) clearTimeout(minuterie.current);
    minuterie.current = setTimeout(() => { void enregistrer(date); }, delai);
  }, [date, enregistrer]);

  // ── Ouverture d'une journée ───────────────────────────────────────────────
  useEffect(() => {
    let vivant = true;
    chargee.current = false;
    const el = zone.current;
    if (el) el.innerHTML = '';
    setEtat('');

    void (async () => {
      try {
        const page = await api.requete<Page>(`/agenda/${date}`);
        if (!vivant) return;
        const cible = zone.current;
        if (cible) cible.innerHTML = page.content;
        dernierEnvoi.current = page.content;
        chargee.current = true;
        setEtat(page.updatedAt ? 'enregistré' : '');
        setErreur(null);
      } catch (e) {
        if (!vivant) return;
        /*
         * Journée illisible : on ne laisse PAS la zone active.
         *
         * Une page blanche activée serait prise pour un effacement au premier
         * enregistrement, et la journée disparaîtrait pour de bon. `chargee`
         * reste donc faux, ce qui bloque toute écriture.
         */
        setEtat('journée illisible');
        setErreur(e instanceof Error ? e.message : 'Lecture impossible');
      }
    })();

    return () => { vivant = false; };
  }, [api, date]);

  // ── Les journées écrites du mois affiché ──────────────────────────────────
  useEffect(() => {
    let vivant = true;
    void (async () => {
      try {
        const jours = await api.requete<string[]>(
          `/agenda/month/${mois.annee}/${mois.mois}`,
        );
        if (vivant) setJoursPleins(new Set(jours));
      } catch {
        // Les pastilles sont un agrément : leur absence n'empêche rien, et une
        // erreur affichée ici couvrirait la page pour un détail.
      }
    })();
    return () => { vivant = false; };
  }, [api, mois.annee, mois.mois]);

  // Le petit calendrier suit la journée ouverte : choisir le 3 mars depuis
  // février doit amener mars, sinon la case choisie n'est plus visible.
  useEffect(() => {
    const d = dateDe(date);
    setMois({ annee: d.getUTCFullYear(), mois: d.getUTCMonth() + 1 });
  }, [date]);

  /**
   * ── Plein écran ─────────────────────────────────────────────────────────
   * Le CADRE de l'agenda, et non la page : la barre latérale et les onglets du
   * navigateur disparaissent, il ne reste que le calendrier, la feuille et la
   * barre d'outils. L'API du navigateur plutôt qu'un simple agrandissement en
   * CSS, parce que c'est elle qui rend l'écran entier — onglets et barre
   * d'adresse compris.
   *
   * L'état suit l'événement du navigateur, pas le clic : Échap, F11 ou un
   * changement d'onglet font sortir du plein écran sans passer par le bouton,
   * et celui-ci doit alors revenir à « Plein écran ».
   */
  const pleinEcranPossible = typeof document !== 'undefined' && document.fullscreenEnabled === true;

  useEffect(() => {
    const element = cadre.current;
    const suivre = () => setPleinEcran(!!element && document.fullscreenElement === element);
    document.addEventListener('fullscreenchange', suivre);
    return () => {
      document.removeEventListener('fullscreenchange', suivre);
      // Quitter l'écran en plein écran — un lien, le retour arrière — ne doit
      // pas laisser le navigateur plein écran sur une autre page.
      if (element && document.fullscreenElement === element) {
        void document.exitFullscreen().catch(() => {});
      }
    };
  }, []);

  async function basculerPleinEcran() {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await cadre.current?.requestFullscreen();
    } catch {
      setErreur('Le navigateur a refusé le plein écran.');
    }
  }

  /**
   * Navigation au clavier, et dernier enregistrement au départ.
   *
   * Les deux écouteurs vivent sur le document et la fenêtre : ils sont posés
   * une fois pour l'écran, et retirés en le quittant.
   */
  useEffect(() => {
    function surTouche(e: KeyboardEvent) {
      const cible = e.target as HTMLElement | null;
      // Ni dans la zone d'écriture, ni dans un champ : les flèches y déplacent
      // le curseur, et changer de journée sous les doigts serait brutal.
      if (cible?.isContentEditable || cible?.tagName === 'INPUT') return;
      if (e.key === 'ArrowLeft') setDate((c) => decaler(c, -1));
      if (e.key === 'ArrowRight') setDate((c) => decaler(c, 1));
    }

    function surDepart() {
      // `keepalive` plutôt que `sendBeacon` : celui-ci n'accepte aucun en-tête,
      // donc ni le jeton ni la classe de travail.
      if (enAttente()) void enregistrer(date, { auDepart: true });
    }

    document.addEventListener('keydown', surTouche);
    window.addEventListener('pagehide', surDepart);
    return () => {
      document.removeEventListener('keydown', surTouche);
      window.removeEventListener('pagehide', surDepart);
    };
  }, [date, enAttente, enregistrer]);

  // Quitter l'écran enregistre ce qui reste en attente.
  useEffect(() => () => {
    if (minuterie.current) clearTimeout(minuterie.current);
  }, []);

  // ── Taille du texte ───────────────────────────────────────────────────────
  function changerTaille(sens: number) {
    const suivante = Math.min(
      TAILLE_MAX,
      Math.max(TAILLE_MIN, taille + sens * TAILLE_PAS),
    );
    setTaille(suivante);
    try { localStorage.setItem(CLE_TAILLE, String(suivante)); } catch { /* sans plus */ }
  }

  /*
   * La réglure suit la taille du texte.
   *
   * Les lignes doivent passer SOUS l'écriture, pas la traverser : l'interligne
   * du fond et celui du texte sont donc la même valeur, calculée ici et nulle
   * part ailleurs.
   */
  const interligne = Math.round(taille * 1.9);
  const styleZone = {
    fontSize: `${taille}px`,
    lineHeight: `${interligne}px`,
    backgroundImage: 'repeating-linear-gradient(to bottom,'
      + ` transparent 0, transparent ${interligne - 1}px,`
      + ` var(--agd-ligne) ${interligne - 1}px, var(--agd-ligne) ${interligne}px)`,
    backgroundSize: `100% ${interligne}px`,
  };

  // ── Couleurs ──────────────────────────────────────────────────────────────
  function ouvrirPalette(mode: 'texte' | 'surligne') {
    if (palette === mode) { setPalette(null); return; }
    /*
     * La sélection est mémorisée AVANT l'ouverture.
     *
     * Cliquer dans la palette fait perdre le focus à la zone : sans cette
     * copie, la couleur ne s'appliquerait à rien.
     */
    const sel = window.getSelection();
    selection.current = sel && sel.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;
    setPalette(mode);
  }

  function appliquerCouleur(couleur: string, mode: 'texte' | 'surligne') {
    const el = zone.current;
    if (!el) return;
    el.focus();
    if (selection.current) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(selection.current);
    }
    document.execCommand('styleWithCSS', false, 'true');

    if (mode === 'texte') {
      const teinte = couleur || COULEURS_TEXTE[0];
      document.execCommand('foreColor', false, teinte);
      setCouleurTexte(teinte);
    } else {
      document.execCommand('hiliteColor', false, couleur || 'transparent');
      setCouleurSurligne(couleur || 'transparent');
      /*
       * On replie la sélection puis on repasse en transparent : sans cela, la
       * frappe qui suit hérite du surlignage et la ligne entière se colore.
       */
      const sel = window.getSelection();
      if (couleur && sel && sel.rangeCount > 0) {
        const plage = sel.getRangeAt(0);
        plage.collapse(false);
        sel.removeAllRanges();
        sel.addRange(plage);
        document.execCommand('hiliteColor', false, 'transparent');
      }
    }
    setPalette(null);
    programmerEnregistrement(600);
  }

  function effacerMiseEnForme() {
    const el = zone.current;
    if (!el) return;
    el.focus();
    document.execCommand('removeFormat');
    programmerEnregistrement(600);
  }

  // ── Écran ─────────────────────────────────────────────────────────────────
  const d = dateDe(date);
  const finDeSemaine = d.getUTCDay() === 0 || d.getUTCDay() === 6;
  const auj = aujourdhui();
  const cases = grilleDuMois(mois.annee, mois.mois);

  async function allerA(cle: string) {
    // La journée quittée est enregistrée avant tout, sans attendre le délai.
    await enregistrer(date);
    setDate(cle);
  }

  return (
    <div className="agenda flex flex-col gap-3">
      {/*
        * Pas d'en-tête de page : l'agenda prend toute la hauteur, et le menu
        * dit déjà où l'on est. Le titre reste pour les lecteurs d'écran, qui
        * s'orientent par les niveaux de titre.
        */}
      <h1 className="sr-only">Agenda</h1>

      {/*
        * ── Toute la hauteur de l'écran ────────────────────────────────────
        * Sur ordinateur, le cadre occupe la fenêtre moins les marges de la
        * coquille, et c'est la feuille qui défile à l'intérieur : la barre
        * d'outils et le calendrier restent sous la main pendant qu'on écrit.
        * Sur téléphone, les deux colonnes s'empilent et la page défile.
        */}
      <div
        ref={cadre}
        className={[
          'flex flex-col overflow-hidden lg:flex-row',
          // En plein écran, le navigateur donne au cadre tout l'écran : ni
          // bordure ni coins arrondis, qui laisseraient un liseré autour.
          pleinEcran
            ? 'h-screen'
            : 'min-h-[34rem] rounded-xl border border-[var(--color-border)] shadow-[var(--ombre-carte)] lg:h-[calc(100dvh-4rem)]',
        ].join(' ')}
      >
        {/* ── Colonne de gauche ──────────────────────────────────────────── */}
        <aside className="agd-cote flex shrink-0 flex-col gap-3.5 px-3 py-3.5 lg:w-[15.25rem]">
          <div>
            <div className="mb-2 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setMois((m) => (m.mois === 1
                  ? { annee: m.annee - 1, mois: 12 }
                  : { ...m, mois: m.mois - 1 }))}
                aria-label="Mois précédent"
                className="size-6 rounded-md bg-white/10 text-base leading-none hover:bg-white/20"
              >
                ‹
              </button>
              <span className="text-[13px] font-bold capitalize tracking-wide">
                {`${MOIS[mois.mois - 1]} ${mois.annee}`}
              </span>
              <button
                type="button"
                onClick={() => setMois((m) => (m.mois === 12
                  ? { annee: m.annee + 1, mois: 1 }
                  : { ...m, mois: m.mois + 1 }))}
                aria-label="Mois suivant"
                className="size-6 rounded-md bg-white/10 text-base leading-none hover:bg-white/20"
              >
                ›
              </button>
            </div>

            {/* Les initiales des jours ne sont pas un en-tête de tableau : le
                calendrier est une grille de boutons, chacun nommé par sa
                date complète. */}
            <div aria-hidden="true" className="agd-cal-entetes">
              {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((j, i) => (
                <span
                  key={`${j}-${i}`}
                  className="pb-0.5 text-center text-[10px] font-bold text-[#7aa0cc]"
                >
                  {j}
                </span>
              ))}
            </div>

            <div className="agd-cal-grille">
              {cases.map((cle, index) => {
                if (cle === null) {
                  return <span key={`vide-${index}`} className="agd-jour vide" />;
                }
                const jour = dateDe(cle).getUTCDay();
                const classes = ['agd-jour'];
                if (jour === 0 || jour === 6) classes.push('we');
                if (cle === auj) classes.push('auj');
                if (cle === date) classes.push('choisi');
                if (joursPleins.has(cle)) classes.push('plein');
                return (
                  <button
                    key={cle}
                    type="button"
                    onClick={() => void allerA(cle)}
                    className={classes.join(' ')}
                    aria-label={libelleJour(cle)}
                    aria-current={cle === date ? 'date' : undefined}
                  >
                    {dateDe(cle).getUTCDate()}
                  </button>
                );
              })}
            </div>
          </div>

          <Bouton
            variante="secondary"
            taille="sm"
            className="w-full"
            onClick={() => void allerA(prochainJourDeClasse(auj))}
          >
            Prochain jour de classe
          </Bouton>
          <button
            type="button"
            onClick={() => void allerA(auj)}
            className="w-full rounded-lg px-2 py-1.5 text-[13px] text-[#cfe0f5] hover:bg-white/10"
          >
            Aujourd’hui
          </button>

          <div className="mt-auto border-t border-white/15 pt-2.5">
            <div className="text-[21px] font-extrabold tracking-wider">
              {d.getUTCFullYear()}
            </div>
            <div className="text-[11px] text-[#7aa0cc]">
              {`semaine ${numeroSemaine(date)}`}
            </div>
          </div>
        </aside>

        {/* ── La page ────────────────────────────────────────────────────── */}
        <div className="relative flex min-w-0 flex-1 flex-col bg-[#eef3fa]">
          <div className="flex min-h-0 flex-1">
            {/* La reliure : vingt anneaux, purement décoratifs. */}
            <div aria-hidden="true" className="agd-reliure w-[26px] shrink-0">
              {Array.from({ length: 20 }, (_, i) => <span key={i} />)}
            </div>

            <div
              className="agd-feuille flex min-h-0 flex-1 flex-col overflow-y-auto px-8 pb-10 pt-5"
            >
              <header className="flex items-start justify-between gap-3.5">
                <div className="flex flex-wrap items-baseline gap-2.5">
                  <div
                    className={[
                      'agd-titre-jour text-[19px] font-bold capitalize',
                      finDeSemaine ? 'text-[#c0392b]' : 'text-[var(--agd-encre)]',
                    ].join(' ')}
                  >
                    {JOURS[d.getUTCDay()]}
                  </div>
                  <div className="agd-titre-jour text-[40px] font-extrabold leading-none text-[var(--agd-encre)]">
                    {d.getUTCDate()}
                  </div>
                  <div className="text-sm capitalize text-[#4a7ec4]">
                    {`${MOIS[d.getUTCMonth()]} ${d.getUTCFullYear()}`}
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => void allerA(decaler(date, -1))}
                    aria-label="Jour précédent"
                    title="Jour précédent (←)"
                    className="size-[34px] rounded-[9px] border-[1.5px] border-[#bcd0e8] bg-white text-base text-[var(--agd-encre)] hover:bg-[var(--agd-encre)] hover:text-white"
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    onClick={() => void allerA(decaler(date, 1))}
                    aria-label="Jour suivant"
                    title="Jour suivant (→)"
                    className="size-[34px] rounded-[9px] border-[1.5px] border-[#bcd0e8] bg-white text-base text-[var(--agd-encre)] hover:bg-[var(--agd-encre)] hover:text-white"
                  >
                    →
                  </button>
                  {/*
                    * Masqué là où le navigateur ne sait pas le faire — Safari
                    * sur iPhone, notamment : un bouton qui ne fait rien est
                    * pire qu'une absence de bouton.
                    */}
                  {pleinEcranPossible && (
                    <button
                      type="button"
                      onClick={() => void basculerPleinEcran()}
                      aria-label={pleinEcran ? 'Quitter le plein écran' : 'Plein écran'}
                      aria-pressed={pleinEcran}
                      title={pleinEcran ? 'Quitter le plein écran (Échap)' : 'Plein écran'}
                      className="ml-2 inline-flex size-[34px] items-center justify-center rounded-[9px] border-[1.5px] border-[#bcd0e8] bg-white text-[var(--agd-encre)] hover:bg-[var(--agd-encre)] hover:text-white"
                    >
                      {pleinEcran ? <IconeQuitterPleinEcran taille={16} /> : <IconePleinEcran taille={16} />}
                    </button>
                  )}
                </div>
              </header>

              <div className={`agd-bande my-3 ${finDeSemaine ? 'agd-bande-we' : ''}`} />

              {/* Dans la feuille, pour rester visible en plein écran. */}
              {erreur && <div className="mb-3"><Message ton="erreur">{erreur}</Message></div>}

              {/*
                * ── La zone d'écriture ──────────────────────────────────────
                * `suppressContentEditableWarning` : React prévient qu'un nœud
                * éditable dont il tient les enfants sera réécrit sous les
                * doigts. C'est précisément ce qu'on évite — la zone est vide
                * pour React, et son contenu est posé par les effets.
                */}
              <div
                ref={zone}
                role="textbox"
                aria-multiline="true"
                aria-label={`Agenda du ${libelleJour(date)}`}
                contentEditable
                suppressContentEditableWarning
                spellCheck
                data-vide="Écrivez librement…"
                // Toute la hauteur de la feuille, dès la page vide : elle
                // occupait 24 rem au milieu de l'écran et ne grandissait
                // qu'avec le texte, comme un champ et non comme une page.
                className="agd-ecrit min-h-[24rem] flex-1"
                style={styleZone}
                onInput={() => programmerEnregistrement()}
                onPaste={(e) => {
                  /*
                   * Collage en TEXTE BRUT.
                   *
                   * Sans cela, un copier-coller depuis un traitement de texte
                   * amène ses polices, ses tailles et ses couleurs, et la page
                   * réglée perd toute unité. Le serveur les écarterait de
                   * toute façon : autant ne pas les afficher d'abord.
                   */
                  e.preventDefault();
                  const texte = e.clipboardData.getData('text/plain');
                  document.execCommand('insertText', false, texte);
                }}
              />
            </div>
          </div>

          {/* ── Barre d'outils ──────────────────────────────────────────── */}
          <div className="flex flex-wrap items-center gap-3.5 border-t-[1.5px] border-[#cddcec] bg-[#f6f9fd] px-3.5 py-2 pl-10">
            <div className="flex items-center gap-1.5">
              <OutilAgenda onClick={() => changerTaille(-1)} titre="Réduire le texte">
                A−
              </OutilAgenda>
              <span className="min-w-[2.6rem] text-center text-[11.5px] text-[#4a7ec4]">
                {`${taille} px`}
              </span>
              <OutilAgenda onClick={() => changerTaille(1)} titre="Agrandir le texte">
                A+
              </OutilAgenda>
            </div>

            <div className="relative flex items-center gap-1.5">
              <OutilAgenda
                onClick={() => ouvrirPalette('texte')}
                titre="Couleur du texte"
                trait={couleurTexte}
              >
                A
              </OutilAgenda>
              <OutilAgenda
                onClick={() => ouvrirPalette('surligne')}
                titre="Surligner"
                trait={couleurSurligne}
              >
                ab
              </OutilAgenda>
              <OutilAgenda
                onClick={effacerMiseEnForme}
                titre="Enlever couleur et surlignage"
              >
                ✕
              </OutilAgenda>

              {palette && (
                <Palette
                  mode={palette}
                  surChoix={(couleur) => appliquerCouleur(couleur, palette)}
                />
              )}
            </div>

            <span className="ml-auto text-[11px] text-[#7aa0cc]">{etat}</span>
          </div>
        </div>
      </div>

    </div>
  );
}

/** Un bouton de la barre d'outils, avec son trait de couleur facultatif. */
function OutilAgenda({
  onClick, titre, trait, children,
}: {
  onClick: () => void;
  titre: string;
  trait?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={titre}
      aria-label={titre}
      className="relative inline-flex h-[30px] min-w-[32px] items-center justify-center rounded-lg border-[1.5px] border-[#bcd0e8] bg-white px-2 text-[13px] font-bold text-[var(--agd-encre)] hover:border-[var(--agd-encre)]"
    >
      {children}
      {trait !== undefined && (
        <span
          aria-hidden="true"
          className="absolute inset-x-1 bottom-[3px] h-[3px] rounded-sm"
          style={{ background: trait }}
        />
      )}
    </button>
  );
}

/**
 * La palette de couleurs.
 *
 * `onMouseDown` avec `preventDefault` plutôt que `onClick` : sans cela, presser
 * le bouton retire le focus de la zone d'écriture AVANT que la couleur ne soit
 * appliquée, et elle ne s'applique à rien.
 */
function Palette({
  mode, surChoix,
}: {
  mode: 'texte' | 'surligne';
  surChoix: (couleur: string) => void;
}) {
  const couleurs = mode === 'texte' ? COULEURS_TEXTE : COULEURS_SURLIGNE;
  return (
    <div
      className="absolute bottom-full left-0 z-50 mb-2 rounded-xl border-[1.5px] border-[#bcd0e8] bg-white p-2.5 shadow-[0_8px_24px_rgba(27,58,107,.22)]"
      role="group"
      aria-label={mode === 'texte' ? 'Couleur du texte' : 'Surlignage'}
    >
      <div className="mb-1.5 text-[11px] font-extrabold text-[#4a7ec4]">
        {mode === 'texte' ? 'Couleur du texte' : 'Surlignage'}
      </div>
      <div className="grid grid-cols-5 gap-1.5">
        {couleurs.map((c) => (
          <button
            key={c}
            type="button"
            onMouseDown={(e) => { e.preventDefault(); surChoix(c); }}
            title={c}
            aria-label={`Teinte ${c}`}
            className="size-6 rounded-md border-[1.5px] border-black/10 hover:scale-110"
            style={{ background: c }}
          />
        ))}
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); surChoix(''); }}
          title="Enlever"
          aria-label="Enlever la couleur"
          className="size-6 rounded-md border-[1.5px] border-black/10 bg-white text-xs text-[#64748b]"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
